import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslations } from "next-intl";
import { IconLock } from "@tabler/icons-react";
import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import { SecondaryButton } from "@/components/Buttons/SecondaryButton";
import { usePreviewStore } from "@/lib/preview/previewStore";
import { usePreviewBlockStore } from "@/lib/preview/previewBlockStore";

/**
 * Read-only notice shown when a write is attempted during a "preview as role"
 * session. Reframes the action honestly: the previewed role CAN perform it, but
 * the preview is read-only — so the user can either exit preview to do it for
 * real, or keep looking around.
 *
 * Mounted ONCE in {@link EventLayoutShell} (next to the preview banner); opened
 * by {@link usePreviewWriteGuard}. Rendered through a portal above the chrome;
 * closes on Escape or backdrop click. Returns null unless a preview session is
 * active AND the notice was opened.
 */
export function PreviewReadOnlyDialog() {
  const t = useTranslations("rolePreview");
  const open = usePreviewBlockStore((s) => s.open);
  const variant = usePreviewBlockStore((s) => s.variant);
  const hide = usePreviewBlockStore((s) => s.hide);
  const active = usePreviewStore((s) => s.active);
  const target = usePreviewStore((s) => s.target);
  const stopPreview = usePreviewStore((s) => s.stopPreview);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") hide();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, hide]);

  // Auto-dismiss if the session ends underneath us (expiry / external stop).
  useEffect(() => {
    if (open && !active) hide();
  }, [open, active, hide]);

  if (!open || !active || !target) return null;

  // Exit preview, then close. stopPreview restores the admin's full-rights view
  // (and remounts the page subtree via the shell's outletKey), so the user can
  // click the action again and have it actually run.
  const onExit = () => {
    hide();
    void stopPreview();
  };

  return createPortal(
    <div
      className="fixed inset-0 z-10001 flex items-center justify-center bg-black/40 px-4"
      onClick={hide}
      role="presentation"
    >
      <div
        className="w-full max-w-100 surface-primary rounded-2xl shadow-xl p-6 flex flex-col gap-4"
        onClick={(e) => e.stopPropagation()}
        role="alertdialog"
        aria-modal="true"
        aria-label={t("blockedToast")}
      >
        <div className="flex items-start gap-3">
          <span className="shrink-0 surface-tertiary rounded-full p-2">
            <IconLock size={18} className="font-secondary" />
          </span>
          <div className="flex flex-col gap-1 min-w-0">
            <p className="h3">{t("blockedToast")}</p>
            <p className="body-3-regular font-secondary">
              {variant === "generic"
                ? t("readOnlyGenericBody")
                : t("readOnlyActionBody", { role: target.name })}
            </p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2">
          <SecondaryButton
            size="small"
            width="w-max"
            text={t("keepPreviewing")}
            onClick={hide}
          />
          <PrimaryButton
            size="small"
            width="w-max"
            icon={IconLock}
            iconSize={16}
            text={t("exit")}
            onClick={onExit}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}
